import React, { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { ReferenceSection } from "../../common";
import { ThemeProps } from "../../styles/theme";
import { renderInlineMarkup } from "../renderInlineMarkup";
import {
    DataCell,
    FullWidthCell,
    HeaderCell,
    TableGrid,
    TableWrapper,
} from "./referenceGrid";
import { getColCount } from "./referenceColumns";
import { hasTrainableTables } from "./trainableTables";

const Root = styled.div`
    width: 100%;
    box-sizing: border-box;
    text-align: left;
`;

const Section = styled.div`
    width: 100%;

    & + & {
        margin-top: 16px;
    }
`;

const SectionHeader = styled.button<ThemeProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 36px;
    padding: 6px 2px;
    box-sizing: border-box;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.theme.secondaryText};
`;

const Chevron = styled.span<ThemeProps & { $isOpen: boolean }>`
    flex-shrink: 0;
    font-size: 10px;
    line-height: 1;
    color: ${props => props.theme.secondaryText};
    transition: transform 0.15s ease;
    transform: rotate(${props => (props.$isOpen ? 180 : 0)}deg);
`;

const TrainButton = styled.a<ThemeProps>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 16px;
    padding: 8px 16px;
    background-color: ${props => props.theme.neutralButtonBackground};
    color: ${props => props.theme.neutralButtonForeground};
    border: ${props => props.theme.neutralButtonBorder} 1px solid;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        opacity: 0.85;
    }
`;

function renderTable(section: ReferenceSection) {
    const colCount = getColCount(section);

    return (
        <TableWrapper>
            <TableGrid $cols={colCount}>
                {section.header !== null && section.header.map((cell, colIdx) => (
                    <HeaderCell key={colIdx} $isFirstRow={true} $isLastCol={colIdx === colCount - 1}>
                        {renderInlineMarkup(cell)}
                    </HeaderCell>
                ))}
                {section.rows.map((row, rowIdx) => {
                    const isFirstRow = rowIdx === 0 && section.header === null;
                    if (row.cells.length !== colCount) {
                        return (
                            <FullWidthCell key={rowIdx} $isFirstRow={isFirstRow}>
                                {row.cells.join(' | ')}
                            </FullWidthCell>
                        );
                    }
                    return row.cells.map((cell, colIdx) => (
                        <DataCell
                            key={`${rowIdx}-${colIdx}`}
                            $isFirstRow={isFirstRow}
                            $isLastCol={colIdx === colCount - 1}
                            $highlight={row.highlight}
                        >
                            {renderInlineMarkup(cell)}
                        </DataCell>
                    ));
                })}
            </TableGrid>
        </TableWrapper>
    );
}

interface ReferenceViewerProps {
    sections: ReferenceSection[];
    defaultExpanded?: boolean;
    cardId?: string;
}

const ReferenceViewerComponent: FC<ReferenceViewerProps> = memo(({ sections, defaultExpanded, cardId }) => {
    const titlesKey = useMemo(() => sections.map(s => s.title).join(' '), [sections]);
    const firstTitledIndex = useMemo(() => {
        const idx = sections.findIndex(s => s.title.length > 0);
        return idx === -1 ? null : idx;
    }, [sections]);
    const [openIndex, setOpenIndex] = useState<number | null>(
        () => defaultExpanded === true ? firstTitledIndex : null
    );

    useEffect(() => {
        setOpenIndex(defaultExpanded === true ? firstTitledIndex : null);
    }, [titlesKey, defaultExpanded, firstTitledIndex]);

    const toggle = useCallback((index: number) => {
        setOpenIndex(prev => (prev === index ? null : index));
    }, []);

    if (sections.length === 0)
        return null;

    const showTrainButton = cardId !== undefined
        && cardId.length > 0
        && hasTrainableTables(sections);

    return (
        <Root>
            {sections.map((section, sectionIndex) => {
                const hasTitle = section.title.length > 0;
                const hasRows = section.rows.length > 0 || section.header !== null;

                if (!hasTitle) {
                    return (
                        <Section key={sectionIndex}>
                            {hasRows && renderTable(section)}
                        </Section>
                    );
                }

                const isOpen = openIndex === sectionIndex;

                return (
                    <Section key={sectionIndex}>
                        <SectionHeader
                            type="button"
                            aria-expanded={isOpen}
                            onClick={() => toggle(sectionIndex)}
                        >
                            {section.title}
                            <Chevron $isOpen={isOpen}>▼</Chevron>
                        </SectionHeader>
                        {isOpen && hasRows && renderTable(section)}
                    </Section>
                );
            })}
            {showTrainButton && (
                <TrainButton
                    href={`/train/${encodeURIComponent(cardId!)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Train ▸
                </TrainButton>
            )}
        </Root>
    );
});

ReferenceViewerComponent.displayName = 'ReferenceViewer';

export const ReferenceViewer = ReferenceViewerComponent;
